import { createFileRoute, notFound } from "@tanstack/react-router"
import OrderConfirmationPage from "@/pages/order-confirmation"
import { ORDER_CONFIRMATION_FIELDS } from "@/lib/constants/order-fields"
import { readOrderConfirmation } from "@/lib/utils/order-confirmation"
import { queryKeys } from "@/lib/utils/query-keys"
import { sanitize } from "@/lib/utils/sanitize"

export const Route = createFileRoute("/$countryCode/order/$orderId/confirmed")({
  validateSearch: (search: Record<string, unknown>): { cart?: string } => ({
    // Per-order confirmation token (the cart id) — lets a guest re-open this
    // receipt after the in-memory copy is gone, via the guest-safe endpoint.
    cart: typeof search.cart === "string" ? search.cart : undefined,
  }),
  loaderDeps: ({ search }) => ({ cart: search.cart }),
  loader: async ({ params, deps, context }) => {
    const { countryCode, orderId } = params
    const { cart } = deps
    const { queryClient } = context
    const { retrieveOrder, retrieveOrderConfirmation } = await import(
      "@/lib/data/order"
    )

    let order = readOrderConfirmation(orderId)

    if (!order) {
      try {
        order = await queryClient.ensureQueryData({
          queryKey: [...queryKeys.orders.detail(orderId), cart ?? "self"],
          queryFn: () =>
            // With a confirmation token, use the guest-safe endpoint (works for
            // guests AND signed-in customers). Without one (e.g. an old link),
            // fall back to the authenticated order endpoint.
            cart
              ? retrieveOrderConfirmation({ order_id: orderId, cart_id: cart })
              : retrieveOrder({
                  order_id: orderId,
                  fields: ORDER_CONFIRMATION_FIELDS,
                }),
        })
      } catch {
        throw notFound()
      }
    }

    if (!order) {
      throw notFound()
    }

    return sanitize({
      countryCode,
      order,
    })
  },
  head: () => ({
    meta: [
      { title: "Order Confirmed | +Bake Cafe" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: OrderConfirmationPage,
})
